import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { StyledColumn, StyledFlex } from "./layout";
import { RouteOutlined } from "../../materialUi/materialUi";
import { Spacer } from "../Spacer";

export interface TripCardProps {
  date: string;
  distance: number;
  departureTime: string;
  departureAddress: string;
  arrivalTime: string;
  arrivalAddress: string;
}

export const TripCard = ({
  date,
  distance,
  departureTime,
  departureAddress,
  arrivalTime,
  arrivalAddress,
}: TripCardProps) => {
  const { t } = useTranslation();

  return (
    <StyledColumn>
      <StyledHeader>
        <StyledHeaderText>{date}</StyledHeaderText>
        <StyledDistance>
          <StyledRouteIcon />
          <Spacer x={0.25} />
          <StyledHeaderText>{distance}</StyledHeaderText>
          <Spacer x={0.25} />
          <StyledHeaderText>{t("tripCard.distanceUnit")}</StyledHeaderText>
        </StyledDistance>
      </StyledHeader>
      <Spacer y={0.5} />
      <StyledCard>
        <Spacer y={1} />
        <StyledFlex>
          <Spacer x={1} />
          <StyledPoints>
            <TripPoint
              dotColor={colors["colors/system/success/success_normal"]}
              time={departureTime}
              address={departureAddress}
              hasConnector
            />
            <TripPoint
              dotColor={colors["colors/button/danger/hover"]}
              time={arrivalTime}
              address={arrivalAddress}
              hasConnector={false}
            />
          </StyledPoints>
          <Spacer x={1} />
        </StyledFlex>
        <Spacer y={1} />
      </StyledCard>
    </StyledColumn>
  );
};

interface TripPointProps {
  dotColor: string;
  time: string;
  address: string;
  hasConnector: boolean;
}

const TripPoint = ({ dotColor, time, address, hasConnector }: TripPointProps) => (
  <StyledFlex>
    <StyledMarker>
      <Spacer y={0.25} />
      <StyledDot $color={dotColor} />
      {hasConnector && <StyledConnector />}
    </StyledMarker>
    <Spacer x={0.75} />
    <StyledPointText>
      <StyledTime>{time}</StyledTime>
      <Spacer y={0.25} />
      <StyledAddress>{address}</StyledAddress>
      {/* Gap to the next point lives inside this point, so the connector spans it. */}
      {hasConnector && <Spacer y={1} />}
    </StyledPointText>
  </StyledFlex>
);

const StyledHeader = styled(StyledFlex)`
  align-items: center;
  justify-content: space-between;
`;

const StyledDistance = styled(StyledFlex)`
  align-items: center;
`;

const StyledHeaderText = styled.span`
  ${typographies["Body/S"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledRouteIcon = styled(RouteOutlined)`
  fill: ${colors["colors/text/darkGrey"]};
`;

const StyledCard = styled(StyledColumn)`
  background: ${colors["colors/surfaces/background/background_level0"]};
  border: 0.0625rem solid ${colors["colors/borders/cards/cards"]};
  border-radius: 0.75rem;
`;

const StyledPoints = styled(StyledColumn)`
  flex: 1;
`;

const StyledMarker = styled(StyledColumn)`
  align-items: center;
`;

const StyledDot = styled.div<{ $color: string }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 25rem;
  background: ${({ $color }) => $color};
`;

// Grows to the bottom of the point, which includes the gap to the next one.
const StyledConnector = styled.div`
  flex: 1;
  width: 0.0625rem;
  background: repeating-linear-gradient(
    to bottom,
    ${colors["colors/borders/separator/cells"]} 0 0.1875rem,
    transparent 0.1875rem 0.375rem
  );
`;

const StyledPointText = styled(StyledColumn)`
  flex: 1;
`;

const StyledTime = styled.span`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]};
`;

const StyledAddress = styled.span`
  ${typographies["Body/XS"]}
  color: ${colors["colors/text/lightGrey"]};
`;
